import { motion } from "motion/react"
import type React from "react"

import locationIconSrc from "@/assets/imgs/map/map-label-location-icon.png"
import { useMapStore } from "@/store/map"
import type { RegionId } from "@/types"
import { px2rem } from "@/utils/adaptFont"
import { BADGE_CONFIG, getRhodesBadgeKey } from "./const"

import styles from "./index.module.scss"

interface Props {
    regionId: RegionId
}

export const MapBadge: React.FC<Props> = ({ regionId }) => {
    const rhodesVariant = useMapStore((s) => s.rhodesVariant)
    const badgeKey =
        regionId === "babel_rhodes"
            ? getRhodesBadgeKey(rhodesVariant)
            : regionId
    const config = BADGE_CONFIG[badgeKey]
    if (!config) return null

    const [logoMl, logoMr] = config.logoMargin ?? [0, 0]
    const cssVars = {
        "--label-h": px2rem(config.labelHeight),
        "--logo-w": px2rem(config.logoWidth),
        "--logo-h": px2rem(config.logoHeight),
        "--logo-ml": px2rem(logoMl),
        "--logo-mr": px2rem(logoMr),
        "--badge-pb": px2rem(config.paddingBottom ?? 0),
    } as React.CSSProperties

    return (
        <motion.div
            className={styles.mapBadge}
            style={cssVars}
            variants={{
                initial: { x: 80, opacity: 0 },
                animate: {
                    x: 0,
                    opacity: 1,
                    transition: {
                        delay: 0.4,
                        type: "spring",
                        stiffness: 380,
                        damping: 28,
                    },
                },
                exit: {
                    x: 80,
                    opacity: 0,
                    transition: { duration: 0.15, ease: "easeIn" },
                },
            }}
            initial="initial"
            animate="animate"
            exit="exit"
        >
            <div className={styles.inner}>
                <div
                    className={styles.labelBg}
                    style={{
                        borderImageSource: `url(${config.labelBg})`,
                        filter: config.locationIconFilter || undefined,
                    }}
                />
                <img
                    className={styles.locationIcon}
                    src={locationIconSrc}
                    alt=""
                    draggable={false}
                    style={
                        config.locationIconFilter
                            ? { filter: config.locationIconFilter }
                            : undefined
                    }
                />
                <img
                    className={styles.logo}
                    src={config.logo}
                    alt=""
                    draggable={false}
                />
                <span className={styles.labelName}>{config.labelName}</span>
            </div>
        </motion.div>
    )
}
